import React from 'react';
import { Mail, Phone } from 'lucide-react';

export const TermsOfServiceContent: React.FC = () => {
  return (
    <div className="space-y-6 text-gray-100">
      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">1. Agreement to Terms</h2>
        <p className="leading-relaxed">
          By accessing and using Lead Wealth A.I.'s services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing our services.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">2. Description of Services</h2>
        <p className="leading-relaxed mb-3">
          Lead Wealth A.I. provides AI-powered business solutions, including but not limited to:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>Custom AI-powered website development and design</li>
          <li>Business process automation and optimization</li>
          <li>AI integration consulting and implementation</li>
          <li>Digital marketing and lead generation solutions</li>
          <li>Technical support and maintenance services</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">3. User Responsibilities</h2>
        <p className="leading-relaxed mb-3">
          As a user of our services, you agree to:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>Provide accurate and complete information when requested</li>
          <li>Maintain the confidentiality of any account credentials</li>
          <li>Use our services only for lawful purposes</li>
          <li>Not attempt to interfere with or disrupt our services</li>
          <li>Respect intellectual property rights and confidentiality agreements</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">4. Payment Terms</h2>
        <p className="leading-relaxed">
          Payment terms are established on a per-project basis. All fees are due as specified in your service agreement. We accept various payment methods as agreed upon during consultation. Late payments may result in service suspension or termination.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">5. Intellectual Property</h2>
        <p className="leading-relaxed">
          Upon full payment, clients receive ownership of custom deliverables created specifically for their project. However, Lead Wealth A.I. retains ownership of proprietary tools, frameworks, and methodologies used in service delivery. We reserve the right to showcase completed projects in our portfolio unless otherwise agreed in writing.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">6. Warranties and Disclaimers</h2>
        <p className="leading-relaxed">
          Our services are provided "as is" without warranties of any kind, either express or implied. While we strive for excellence, we do not guarantee specific business results or outcomes. We make every effort to ensure our services meet high-quality standards but cannot guarantee uninterrupted or error-free operation.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">7. Limitation of Liability</h2>
        <p className="leading-relaxed">
          Lead Wealth A.I. shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services. Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">8. Service Modifications and Termination</h2>
        <p className="leading-relaxed">
          We reserve the right to modify, suspend, or discontinue any aspect of our services at any time. Either party may terminate service agreements according to terms specified in individual contracts. Upon termination, you remain responsible for any outstanding payments.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">9. Confidentiality</h2>
        <p className="leading-relaxed">
          We respect the confidentiality of information shared during our business relationship. Both parties agree to maintain confidentiality of proprietary and sensitive information disclosed during the course of service delivery.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">10. Governing Law</h2>
        <p className="leading-relaxed">
          These Terms of Service shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising from these terms shall be resolved through good faith negotiation or, if necessary, through appropriate legal channels.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">11. Changes to Terms</h2>
        <p className="leading-relaxed">
          We reserve the right to update these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after any changes constitutes acceptance of the new terms.
        </p>
      </section>

      <p className="text-sm text-gray-300 text-center mt-8 pt-6 border-t border-white/20">
        Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <div className="mt-8 text-center pt-6 border-t border-white/20">
        <h3 className="text-xl font-semibold mb-4 text-yellow-400">Contact Us</h3>
        <p className="text-gray-300 mb-4 text-sm">
          If you have any questions about these Terms of Service, please contact us:
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="mailto:admin@leadwealth.ai"
            className="flex items-center text-gray-200 hover:text-yellow-400 transition-colors group text-sm"
          >
            <Mail className="w-4 h-4 mr-2 text-yellow-400 group-hover:scale-110 transition-transform" />
            admin@leadwealth.ai
          </a>
          <a
            href="tel:4098935232"
            className="flex items-center text-gray-200 hover:text-yellow-400 transition-colors group text-sm"
          >
            <Phone className="w-4 h-4 mr-2 text-yellow-400 group-hover:scale-110 transition-transform" />
            409-893-5232
          </a>
        </div>
      </div>
    </div>
  );
};
